import React, {
    FocusEvent,
    FocusEventHandler,
    memo,
    ReactNode,
    useCallback,
    useState,
    FC,
} from 'react';
import {
    Autocomplete,
    autocompleteClasses,
    AutocompleteInputChangeReason,
    AutocompleteProps,
    outlinedInputClasses,
    SxProps,
    Theme,
} from '@mui/material';

import { DropdownOption } from '../Dropdown';
import { Stack } from '../Stack';
import { Typography } from '../Typography';
import { Search2 } from '../Search2';
import { MiniClose24, Search24 } from '../../icons';
import { useOpenState } from '../DropdownButton';
import { boxShadows } from '../../palette';


type AutocompleteSearchFilterProps<T> = {
    value: DropdownOption<T>;
    onChange: (value: DropdownOption<T> | string) => void;
    options: DropdownOption<T>[];
    inputValue?: string;
    onInputChange?: (value?: string, reason?: AutocompleteInputChangeReason) => void;
    openState: ReturnType<typeof useOpenState>;
    helperText?: ReactNode;
    placeholder?: string;
    onFocus?: FocusEventHandler<HTMLInputElement>;
    onBlur?: FocusEventHandler<HTMLInputElement>;
    startIcon?: ReactNode;
    label?: ReactNode;
    loading?: boolean;
    noOptionsText?: ReactNode;
    loadingText?: ReactNode;
    size?: 'large' | 'medium';
    customRenderOption?: AutocompleteProps<DropdownOption<T>, false, false, false>['renderOption'];
    getCustomOptionLabel?: (option: DropdownOption<T> | string) => string;
    allowFreeInput?: boolean;
    type?: 'number';
    paperSx?: SxProps<Theme>;
    error?: boolean;
    onKeyPress?: (e: any) => void;
    maxLength?: number;
};


const useFocused = () => {
    const [isFocused, setFocus] = useState(false);
    const onFocus = useCallback(() => setFocus(true), []);
    const onBlur = useCallback(() => setFocus(false), []);
    return {
        isFocused,
        onFocus,
        onBlur,
    };
};


const PaperComponent: FC<React.HTMLAttributes<HTMLElement>> = props => (
    <Stack
        {...props}
        p={2}
        sx={{
            backgroundColor: 'grayscale.shade0',
            borderRadius: '16px',
        }}
    />
);


const AutocompleteSearchFilterComponent = <T extends (ReactNode | Record<string, string>)>(
    props: AutocompleteSearchFilterProps<T> & { children?: ReactNode },
) => {
    const {
        value,
        onChange,
        inputValue,
        onInputChange,
        options,
        openState,
        helperText,
        placeholder,
        startIcon,
        label,
        loading = false,
        noOptionsText,
        loadingText,
        size = 'large',
        customRenderOption,
        getCustomOptionLabel,
        allowFreeInput = false,
        type,
        paperSx,
        error = false,
        maxLength,
    } = props;
    const { isFocused, onFocus, onBlur } = useFocused();

    const onChangeMemoized = useCallback((_, v) => onChange(v), [onChange]);

    const onInputChangeMemoized = useCallback(
        (_, newValue, reason) => onInputChange(newValue, reason), [onInputChange],
    );

    const onInputCastomChangeMemoized = useCallback((_, newValue, reason) => {
        onInputChange(newValue, reason);
        onChange(newValue);
    }, [onInputChange, onChange]);

    return (
        <Autocomplete<DropdownOption<T>, false, true, true>
            onInputChange={onInputChangeMemoized}

            {...allowFreeInput && {
                freeSolo: true,
                onInputChange: onInputCastomChangeMemoized,
            }}

            value={value}
            inputValue={inputValue}
            onChange={onChangeMemoized}
            onBlur={props.onBlur}
            onFocus={props.onFocus}

            isOptionEqualToValue={(option, newValue) => option.value === newValue.value}
            filterOptions={option => option}

            loading={loading}
            options={options}
            open={openState.isOpen}
            blurOnSelect

            clearIcon={value ? (
                <MiniClose24
                    sx={({ palette }) => ({
                        fill: palette.grayscale.shade95,
                        fontSize: '24px',
                    })}
                />
            ) : (
                <Search24
                    sx={({ palette }) => ({
                        fill: palette.grayscale.shade85,
                        fontSize: '24px',
                    })}
                />
            )}
            forcePopupIcon={false}
            autoHighlight

            PaperComponent={PaperComponent}

            componentsProps={{
                clearIndicator: {
                    sx: {
                        visibility: 'visible',
                        '&:hover': {
                            backgroundColor: 'transparent',
                        },
                    },
                },
                popper: {
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 8],
                            },
                        },
                    ],
                    sx: {
                        [`& .${autocompleteClasses.noOptions}`]: {
                            p: 0,
                        },
                        [`& .${autocompleteClasses.listbox}`]: {
                            p: 0,
                        },
                    },
                },
                paper: {
                    sx: {
                        boxShadow: boxShadows.shadow1,
                        borderRadius: 4,

                        ...paperSx,
                    },
                },
            }}

            sx={({ spacing }) => ({
                [`& .${autocompleteClasses.clearIndicator}`]: {
                    p: 0,
                    mr: 0,
                },
                [`& .${autocompleteClasses.inputRoot} .${autocompleteClasses.endAdornment}`]: {
                    top: '50%',
                    right: spacing(4),
                    transform: 'translate(0, -50%)',
                },
                [`& .${outlinedInputClasses.root}`]: {
                    pt: size === 'large' ? 6 : 5,
                    pb: size === 'large' ? 2 : 1,
                    pl: 4,

                    [`& .${autocompleteClasses.input}`]: {
                        px: 0,
                        pb: 0,
                        pt: 0,
                        height: spacing(6),
                    },
                },
            })}
            noOptionsText={noOptionsText}
            loadingText={loadingText}
            renderInput={({
                size: inputSize,
                InputProps: { startAdornment, ...InputProps },
                ...props
            }) => (
                <Stack spacing={2}>
                    <Search2
                        {...InputProps}
                        {...{
                            ...props,
                            inputProps: {
                                ...props.inputProps,
                                maxLength,
                            },
                        }}
                        type={type}
                        size={size}
                        isFocused={isFocused}
                        label={label}
                        isGray
                        startIcon={startIcon}
                        placeholder={placeholder}
                        value={props.inputProps.value}
                        onChange={props.inputProps.onChange}
                        onClick={openState.change}

                        onBlur={e => {
                            if (onBlur)
                                onBlur();
                            props.inputProps.onBlur(
                                e as FocusEvent<HTMLInputElement>,
                            );
                            openState.close();
                        }}
                        onFocus={e => {
                            if (onFocus)
                                onFocus();
                            props.inputProps.onFocus(
                                e as FocusEvent<HTMLInputElement>,
                            );
                        }}

                        error={error}
                    />

                    {helperText && (
                        <Typography
                            variant='regularCaption3'
                            color='infoCustom.red60'
                            px={4}
                        >
                            {helperText}
                        </Typography>
                    )}
                </Stack>
            )}

            {...getCustomOptionLabel && {
                getOptionLabel: getCustomOptionLabel,
            }}

            renderOption={customRenderOption ?? (({ className, ...props }, option) => (
                <Stack
                    {...props}
                    component='li'
                    borderRadius={2}
                    sx={{
                        p: 2,
                        cursor: 'pointer',
                        [`&:hover, & .${autocompleteClasses.focused}`]: {
                            bgcolor: 'grayscale.shade5',
                        },
                        [`& .${autocompleteClasses.focused}`]: {
                            bgcolor: 'grayscale.shade10',
                        },
                    }}
                >
                    <Typography
                        variant='regularCaption1'
                        color='grayscale.shade95'
                    >
                        {option.label}
                    </Typography>
                    {option.description && (
                        <Typography
                            variant='regularCaption1'
                            color='grayscale.shade85'
                        >
                            {option.description}
                        </Typography>
                    )}
                </Stack>
            ))}
        />
    );
};

export const AutocompleteSearchFilter = memo(
    AutocompleteSearchFilterComponent,
) as <T extends (ReactNode | Record<string, string>)>(
    props: AutocompleteSearchFilterProps<T>,
) => JSX.Element;
